"use client";

import { c as _c } from "react/compiler-runtime";
import { IconX } from "@tabler/icons-react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { trackSearchSubmitted } from "@/lib/analytics/segment";
import { scrubQueryClient } from "@/lib/search/clientPiiScrub";
import { usePrefetchSearchSuggestions, useSearchSuggestions } from "@/queries/useSearchSuggestions";
import { useSearchPanelStore } from "@/stores/useSearchPanelStore";
import { SearchAutocomplete } from "./SearchAutocomplete";
import { SearchEmptyState } from "./SearchEmptyState";
import { SearchInputBar } from "./SearchInputBar";
interface Props {
  autoFocus?: boolean;
}
const DEBOUNCE_MS = 150;
function resolveResultCount(data: ReturnType<typeof useSearchSuggestions>["data"]): number {
  if (!data) return 0;
  if (data.kind === "results") return data.total;
  return 0;
}
export function SearchPanelBody(t0) {
  const $ = _c(25);
  const {
    autoFocus
  } = t0;
  const router = useRouter();
  const query = useSearchPanelStore(_temp);
  const setQuery = useSearchPanelStore(_temp2);
  const close = useSearchPanelStore(_temp3);
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
  let t1;
  if ($[0] !== debouncedQuery) {
    t1 = debouncedQuery.trim();
    $[0] = debouncedQuery;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: suggestionsData
  } = useSearchSuggestions(t1);
  const prefetchSuggestions = usePrefetchSearchSuggestions();
  let t2;
  if ($[2] !== close || $[3] !== debouncedQuery || $[4] !== prefetchSuggestions || $[5] !== query || $[6] !== router || $[7] !== suggestionsData) {
    t2 = function handleSubmit() {
      const trimmed = query.trim();
      if (trimmed.length === 0) {
        return;
      }
      const safe = scrubQueryClient(trimmed);
      prefetchSuggestions(trimmed);
      trackSearchSubmitted({
        query: safe,
        resultCount: resolveResultCount(suggestionsData),
        source: "submit"
      });
      close();
      if (debouncedQuery.trim() === trimmed && suggestionsData?.kind === "product-request") {
        router.push(`/product-requests?q=${encodeURIComponent(safe)}`);
        return;
      }
      router.push(`/products?q=${encodeURIComponent(safe)}`);
    };
    $[2] = close;
    $[3] = debouncedQuery;
    $[4] = prefetchSuggestions;
    $[5] = query;
    $[6] = router;
    $[7] = suggestionsData;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleSubmit = t2;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <IconX className="size-5" />;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== close) {
    t4 = <Button type="button" variant="ghost" size="icon" aria-label="Close search" className="absolute top-3 right-3 size-8 shrink-0" onClick={close}>{t3}</Button>;
    $[10] = close;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== autoFocus || $[13] !== handleSubmit || $[14] !== query || $[15] !== setQuery) {
    t5 = <SearchInputBar value={query} onChange={setQuery} onSubmit={handleSubmit} autoFocus={autoFocus} />;
    $[12] = autoFocus;
    $[13] = handleSubmit;
    $[14] = query;
    $[15] = setQuery;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== debouncedQuery) {
    t6 = <div className="min-h-64 flex-1" data-testid="search-content-slot">{debouncedQuery.trim().length === 0 ? <SearchEmptyState /> : <SearchAutocomplete query={debouncedQuery.trim()} />}</div>;
    $[17] = debouncedQuery;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <div className="flex flex-col gap-4 p-6 pt-12">{t5}{t6}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t4 || $[23] !== t7) {
    t8 = <div className="relative flex h-full flex-col">{t4}{t7}</div>;
    $[22] = t4;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function _temp3(state_1) {
  return state_1.close;
}
function _temp2(state_0) {
  return state_0.setQuery;
}
function _temp(state) {
  return state.query;
}